import { Link } from 'react-router-dom';
import { Card, Stack, Title, Text } from '@repletoryreach/sdk-ui';
import { StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import type { admin } from '@repletoryreach/sdk-api';
import { formatDateTime, formatRelative } from '../../../../lib/format';
import { CopyableId } from '../../../../components/CopyableId';
import { TenantName } from '../../../../components/TenantName';
import { useT } from '../../../../i18n/I18nProvider';

type AdminUser = NonNullable<Awaited<ReturnType<typeof admin.listUsers>>['data']>[number];

const MUTED = 'var(--rr-color-text-muted)';

export function AccountTab({ user }: { user: AdminUser }) {
  const t = useT();
  return (
    <Stack gap="lg">
      <Card withBorder>
        <Stack gap="md">
          <Title order={4}>{t('users.view.account.title')}</Title>
          <Stack gap="sm">
            <Row label={t('common.email')} value={user.email ?? <Text fz="sm" c={MUTED}>{t('users.noEmail')}</Text>} mono={Boolean(user.email)} />
            <Row
              label={t('users.columns.displayName')}
              value={user.display_name ?? <Text fz="sm" c={MUTED}>{t('users.unnamed')}</Text>}
            />
            <Row
              label={t('users.filters.scope')}
              value={
                <StatusBadge size="xs" status={user.scope === 'platform' ? 'warning' : 'pending'}>
                  {user.scope === 'platform' ? t('users.scope.platform') : t('users.scope.tenant')}
                </StatusBadge>
              }
            />
            <Row
              label={t('common.tenant')}
              value={
                <Link to={`/tenants/${user.organisation_id}`} style={{ color: 'var(--rr-color-primary)', textDecoration: 'none' }}>
                  <TenantName id={user.organisation_id} />
                </Link>
              }
            />
            <Row label={t('common.created')} value={`${formatDateTime(user.created_at)} (${formatRelative(user.created_at)})`} />
            {/* Support does quote a user id, so it stays: as something to copy,
                not as the row that identifies the person. */}
            <Row label={t('users.view.account.userId')} value={<CopyableId value={user.id} label={t('users.view.copyUserId')} chars={36} />} />
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
}

/** The value slot holds components that render their own paragraph, so it is a plain block. */
function Row({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 8, alignItems: 'baseline' }}>
      <Text fz="xs" c={MUTED}>{label}</Text>
      <div style={{ fontSize: 14, ...(mono ? { fontFamily: 'var(--rr-font-mono, ui-monospace, monospace)' } : null) }}>
        {value}
      </div>
    </div>
  );
}
